import { FC, useRef, useState, useEffect } from 'react';
import SupplierDatabase from '../app-supplier-database/supplier-database';
import { serviceAreas } from '../../services/service-areas';
import { ServiceSupplier } from '../../services/service-supplier';
import styles from './page-area-supplier-assign.module.scss';
import { useHistory } from 'react-router-dom';
import { NotificationManager } from 'react-notifications';
import { summarizeAreaAssignment } from '../../util/supplierBulkOutcome';

type PageAreaSupplierAssignProps = {
  showLoading: boolean;
  onSaveClick: () => void;
  match: any;
  area?: { label: string; value: string };
  t: any;
}

const PageAreaSupplierAssign: FC<PageAreaSupplierAssignProps> = ({
  showLoading,
  match,
  area: routeArea,
  t
}) => {
  const [suppliers, setSuppliers] = useState<string[]>([]);
  const [previousSuppliers, setPreviousSuppliers] = useState([]);
  const supplierDatabaseInstance = useRef();
  const [area, setArea] = useState<any>(routeArea);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const history = useHistory();

  useEffect(() => {
    const fetchAreaAndAssignedSuppliers = async () => {
      if (match?.params?.areaId) {

        if (!routeArea) {
          const allAreas = await serviceAreas.getAreas();
          const foundArea = allAreas?.find((a: any) => a._id === match.params.areaId);
          if (foundArea) {
            setArea({ label: foundArea.name, value: foundArea._id });
          }
        }
        const filteredSuppliers = await ServiceSupplier.fetchSupplier({ areaId: match.params.areaId });
        setPreviousSuppliers(filteredSuppliers.data);
      }
      setLoading(false);
    };
    fetchAreaAndAssignedSuppliers();
  }, [match?.params?.areaId, routeArea]);

  const selectEventHandler = (selectedSupplier: any) => {
    setSuppliers(selectedSupplier);
  };

  const initialSupplierIds = previousSuppliers.map((s: any) => s.sup_id);
  const hasChanges =
    initialSupplierIds.length !== suppliers.length ||
    suppliers.some((id: string) => !initialSupplierIds.includes(id)) ||
    initialSupplierIds.some((id: string) => !suppliers.includes(id));

  const elSaveClickCallback = async () => {
    const mappedSuppliers = previousSuppliers.map((supplier: any) => supplier.sup_id);
    const suppliersToRemove = mappedSuppliers.filter((supplier: any) => !suppliers.includes(supplier));
    const suppliersToAdd = suppliers.filter((supplier: any) => !mappedSuppliers.includes(supplier));

    if (!suppliersToRemove.length && !suppliersToAdd.length) {
      NotificationManager.error(
        t('notification.error.unexpected.noChanges', 'Inga ändringar att spara'),
        t('notification.error.unexpected.title', 'Misslyckat')
      );
      return;
    }

    setSaving(true);
    // One bulk request per direction rather than a PUT per supplier: those routes are
    // org-scoped and report what they actually wrote, so a half that fails can be told
    // apart from a half that went through.
    const areaIds = [area?.value];
    const [removeOutcome, addOutcome] = await Promise.allSettled([
      suppliersToRemove.length ? ServiceSupplier.bulkRemoveAreas(suppliersToRemove, areaIds) : Promise.resolve(null),
      suppliersToAdd.length ? ServiceSupplier.bulkAddAreas(suppliersToAdd, areaIds) : Promise.resolve(null),
    ]);
    setSaving(false);

    const { added, removed, skippedIds, failedDirections } = summarizeAreaAssignment(removeOutcome, addOutcome);

    const changes = [];
    if (added > 0) {
      changes.push(t('areaSupplierAssign.notification.added', {
        count: added,
        defaultValue_one: '{{count}} leverantör lades till',
        defaultValue_other: '{{count}} leverantörer lades till',
      }));
    }
    if (removed > 0) {
      changes.push(t('areaSupplierAssign.notification.removed', {
        count: removed,
        defaultValue_one: '{{count}} leverantör togs bort',
        defaultValue_other: '{{count}} leverantörer togs bort',
      }));
    }
    if (changes.length) {
      NotificationManager.success(changes.join(', '), t('notification.success.saved', 'Ändringar sparade'));
    }
    if (skippedIds.length) {
      NotificationManager.info(t('areaSupplierAssign.notification.skipped', {
        count: skippedIds.length,
        defaultValue_one: '{{count}} leverantör hoppades över (borttagen eller otillgänglig)',
        defaultValue_other: '{{count}} leverantörer hoppades över (borttagna eller otillgängliga)',
      }));
    }
    failedDirections.forEach(direction => {
      NotificationManager.error(
        direction === 'add'
          ? t('areaSupplierAssign.notification.addFailed', 'Leverantörerna kunde inte läggas till i kategorin.')
          : t('areaSupplierAssign.notification.removeFailed', 'Leverantörerna kunde inte tas bort från kategorin.'),
        t('notification.error.unexpected.title', 'Misslyckat')
      );
    });
    if (!changes.length && !skippedIds.length && !failedDirections.length) {
      NotificationManager.info(t('areaSupplierAssign.notification.nothingChanged', 'Inga ändringar gjordes.'));
    }

    // Stay on the page when a half failed, so the selection survives for a retry.
    if (!failedDirections.length) {
      history.goBack();
    }
  };

  return !loading ? (
    <div className={styles.container}>
      <SupplierDatabase
        ref={supplierDatabaseInstance}
        key={1}
        areaName={area?.label}
        selectMode={true}
        selectEvent={selectEventHandler}
        onSaveSelectedSuppliersClick={elSaveClickCallback}
        showLoading={showLoading || saving}
        previousSuppliers={previousSuppliers}
        canRemoveSuppliers={true}
        saveDisabled={!hasChanges}
        backButtonUrl={`/settings/categories`}
      />
    </div>
  ) : null;
};

export default PageAreaSupplierAssign;
